import { useEffect, useId, useState } from 'react';
import { MAX_CANVAS_DIMENSION, MAX_CANVAS_PIXELS, normalizeScreenName } from '../config';
import { LED_COLOR_PRESETS, PROJECTION_COLOR_PRESETS, RESOLUTION_PRESETS } from '../presets';
import {
  getMaximumProjectionDimension,
  toggleProjectionAspectLock,
  updateProjectionResolution,
} from '../projectionGeometry';
import { calculateProjectionGridLayout } from '../renderer';
import type { GeneratorConfig } from '../types';
import { parseNumberDraft, validateHexColor, validateNumberDraft } from '../validation';
import { CollapsibleControlSection } from './CollapsibleControlSection';

interface ControlsProps {
  config: GeneratorConfig;
  updateConfig: (updater: (current: GeneratorConfig) => GeneratorConfig) => void;
  onLogoUpload: (file: File) => void;
  allowPowerRoutes?: boolean;
  modeControl?: 'sidebar' | 'header' | 'locked';
}

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  integer?: boolean;
}

type PatternControlSectionId = 'screen' | 'geometry' | 'pattern' | 'power' | 'label' | 'logo';

export function Controls({
  config,
  updateConfig,
  onLogoUpload,
  allowPowerRoutes = true,
  modeControl = 'sidebar',
}: ControlsProps) {
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [routeNameDraft, setRouteNameDraft] = useState('');
  const [openSection, setOpenSection] = useState<PatternControlSectionId | null>('screen');
  const updateProjection = (patch: Partial<GeneratorConfig['projection']>) =>
    updateConfig((current) => ({
      ...current,
      projection: { ...current.projection, ...patch },
    }));
  const changeProjectionResolution = (change: Parameters<typeof updateProjectionResolution>[1]) =>
    updateConfig((current) => ({
      ...current,
      projection: updateProjectionResolution(current.projection, change),
    }));
  const updateLed = (patch: Partial<GeneratorConfig['led']>) =>
    updateConfig((current) => ({ ...current, led: { ...current.led, ...patch } }));
  const updatePower = (patch: Partial<GeneratorConfig['power']>) =>
    updateConfig((current) => ({ ...current, power: { ...current.power, ...patch } }));
  const updateLabel = (patch: Partial<GeneratorConfig['label']>) =>
    updateConfig((current) => ({ ...current, label: { ...current.label, ...patch } }));
  const updateLogo = (patch: Partial<GeneratorConfig['logo']>) =>
    updateConfig((current) => ({ ...current, logo: { ...current.logo, ...patch } }));
  const unitFactor = config.displayUnit === 'm' ? 1000 : 1;
  const unitStep = config.displayUnit === 'm' ? 0.001 : 1;
  const maximumMeasurement = Number.MAX_SAFE_INTEGER / unitFactor;
  const maximumProjectionWidth = getMaximumProjectionDimension(config.projection, 'width');
  const maximumProjectionHeight = getMaximumProjectionDimension(config.projection, 'height');
  const projectionGridLayout = calculateProjectionGridLayout(config.projection, {
    resolution: config.projection.resolution,
    physical: config.projection.physical,
    estimatedBytes: config.projection.resolution.width * config.projection.resolution.height * 4,
  });
  const gridDefinitionMode = config.projection.gridPhysicalEnabled
    ? 'physical'
    : config.projection.gridMode === 'fit'
      ? 'divisions'
      : 'pixels';
  const pixelSquareCells =
    config.projection.gridMode === 'square-from-x' ||
    config.projection.gridMode === 'square-from-y';
  const physicalSquareCells =
    Math.abs(config.projection.gridPhysicalWidthMm - config.projection.gridPhysicalHeightMm) < 1e-6;
  const maximumPhysicalSquareMm = Math.max(
    1,
    Math.min(config.projection.physical.widthMm, config.projection.physical.heightMm) / 2,
  );
  const minimumPhysicalSquareMm = Math.min(
    maximumPhysicalSquareMm,
    Math.max(
      1,
      config.projection.physical.widthMm / 256,
      config.projection.physical.heightMm / 256,
    ),
  );
  const effectiveGridColumns = config.projection.gridLinkCounts
    ? config.projection.gridDensity
    : config.projection.gridColumns;
  const effectiveGridRows = config.projection.gridLinkCounts
    ? config.projection.gridDensity
    : config.projection.gridRows;
  const physicalGridStepX =
    (projectionGridLayout.stepX / config.projection.resolution.width) *
    (config.projection.physical.widthMm / unitFactor);
  const physicalGridStepY =
    (projectionGridLayout.stepY / config.projection.resolution.height) *
    (config.projection.physical.heightMm / unitFactor);
  const maximumSquareCellPx = Math.max(
    1,
    Math.min(config.projection.resolution.width, config.projection.resolution.height) / 2,
  );
  const minimumSquareCellPx = Math.min(
    maximumSquareCellPx,
    Math.max(
      1,
      config.projection.resolution.width / 256,
      config.projection.resolution.height / 256,
    ),
  );
  const minimumCellWidthPx = Math.min(
    Math.max(1, config.projection.resolution.width / 2),
    Math.max(1, config.projection.resolution.width / 256),
  );
  const minimumCellHeightPx = Math.min(
    Math.max(1, config.projection.resolution.height / 2),
    Math.max(1, config.projection.resolution.height / 256),
  );
  const ledPixelCount =
    config.led.cabinetResolution.width *
    config.led.cabinetResolution.height *
    config.led.columns *
    config.led.rows;
  const maximumCabinetWidth = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.columns),
    Math.floor(
      (MAX_CANVAS_PIXELS * config.led.cabinetResolution.width) / Math.max(1, ledPixelCount),
    ),
  );
  const maximumCabinetHeight = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.rows),
    Math.floor(
      (MAX_CANVAS_PIXELS * config.led.cabinetResolution.height) / Math.max(1, ledPixelCount),
    ),
  );
  const maximumColumns = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.cabinetResolution.width),
    Math.floor((MAX_CANVAS_PIXELS * config.led.columns) / Math.max(1, ledPixelCount)),
  );
  const maximumRows = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.cabinetResolution.height),
    Math.floor((MAX_CANVAS_PIXELS * config.led.rows) / Math.max(1, ledPixelCount)),
  );
  const patternPresetLabel =
    (config.mode === 'projection' ? PROJECTION_COLOR_PRESETS : LED_COLOR_PRESETS).find(
      (preset) =>
        preset.id ===
        (config.mode === 'projection' ? config.projection.colorPreset : config.led.colorPreset),
    )?.label ?? 'Custom';

  const toggleSection = (sectionId: PatternControlSectionId) => {
    setOpenSection((current) => (current === sectionId ? null : sectionId));
  };
  const saveRouteName = (routeId: string) => {
    const cableLabel = routeNameDraft.trim();
    updatePower({
      routes: config.power.routes.map((route) =>
        route.id === routeId ? { ...route, cableLabel } : route,
      ),
    });
    setEditingRouteId(null);
    setRouteNameDraft('');
  };

  return (
    <div className="controls pattern-controls" aria-label="Pattern settings">
      <CollapsibleControlSection
        title="Screen"
        summary={`${config.title.trim() || 'Untitled screen'} · ${config.mode === 'led' ? 'LED wall' : 'Projection'}`}
        badge={config.mode === 'led' ? 'LED' : 'Projection'}
        isOpen={openSection === 'screen'}
        onToggle={() => toggleSection('screen')}
        intro
      >
        {modeControl === 'sidebar' ? (
          <div className="segmented" aria-label="Pattern mode">
            <button
              className={config.mode === 'led' ? 'is-active' : ''}
              type="button"
              onClick={() => updateConfig((current) => ({ ...current, mode: 'led' }))}
            >
              LED wall
            </button>
            <button
              className={config.mode === 'projection' ? 'is-active' : ''}
              type="button"
              onClick={() => updateConfig((current) => ({ ...current, mode: 'projection' }))}
            >
              Projection
            </button>
          </div>
        ) : modeControl === 'locked' ? (
          <div className="canvas-screen-mode">
            {config.mode === 'led' ? 'LED Canvas screen' : 'Projection Canvas item'}
          </div>
        ) : null}
        <label className="field">
          <span>Screen / output name</span>
          <input
            value={config.title}
            maxLength={120}
            onChange={(event) =>
              updateConfig((current) => ({ ...current, title: event.target.value }))
            }
            onBlur={() =>
              updateConfig((current) => ({
                ...current,
                title: normalizeScreenName(current.title),
              }))
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
        </label>
        <label className="field">
          <span>Physical size display</span>
          <select
            value={config.displayUnit}
            onChange={(event) =>
              updateConfig((current) => ({
                ...current,
                displayUnit: event.target.value as GeneratorConfig['displayUnit'],
              }))
            }
          >
            <option value="mm">Millimetres (mm)</option>
            <option value="m">Metres (m)</option>
          </select>
        </label>
      </CollapsibleControlSection>

      {config.mode === 'projection' ? (
        <CollapsibleControlSection
          title="Projection geometry"
          summary={`${config.projection.resolution.width} × ${config.projection.resolution.height} px · ${formatPhysicalSize(config.projection.physical.widthMm, config.projection.physical.heightMm, config.displayUnit)}`}
          isOpen={openSection === 'geometry'}
          onToggle={() => toggleSection('geometry')}
        >
          <div className="control-subheading projection-geometry-heading">
            <span>Resolution</span>
            <button
              type="button"
              className={`projection-geometry-lock ${
                config.projection.resolutionAspectLocked ? 'is-active' : ''
              }`}
              aria-pressed={config.projection.resolutionAspectLocked}
              title="Keep the current resolution aspect ratio"
              onClick={() =>
                updateConfig((current) => ({
                  ...current,
                  projection: toggleProjectionAspectLock(current.projection),
                }))
              }
            >
              <span aria-hidden="true">
                {config.projection.resolutionAspectLocked ? '🔒' : '🔓'}
              </span>
              {config.projection.resolutionAspectLocked
                ? `${config.projection.resolutionAspectRatio.width}:${config.projection.resolutionAspectRatio.height}`
                : 'Lock aspect'}
            </button>
          </div>
          <label className="field">
            <span>Resolution preset</span>
            <select
              value={presetValue(
                config.projection.resolution.width,
                config.projection.resolution.height,
              )}
              onChange={(event) => {
                const preset = RESOLUTION_PRESETS.find(
                  (item) => `${item.width}x${item.height}` === event.target.value,
                );
                if (preset?.width && preset.height) {
                  changeProjectionResolution({
                    kind: 'preset',
                    resolution: { width: preset.width, height: preset.height },
                  });
                }
              }}
            >
              {RESOLUTION_PRESETS.map((preset) => (
                <option key={preset.label} value={`${preset.width}x${preset.height}`}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>
          <div className="field-row">
            <NumberField
              label="Width (px)"
              value={config.projection.resolution.width}
              min={1}
              max={maximumProjectionWidth}
              step={1}
              integer
              onChange={(value) => changeProjectionResolution({ kind: 'width', value })}
            />
            <NumberField
              label="Height (px)"
              value={config.projection.resolution.height}
              min={1}
              max={maximumProjectionHeight}
              step={1}
              integer
              onChange={(value) => changeProjectionResolution({ kind: 'height', value })}
            />
          </div>
          <div className="control-subheading control-subheading--spaced projection-geometry-heading">
            <span>Physical size</span>
            <button
              type="button"
              className={`projection-geometry-lock ${
                config.projection.physicalFollowsResolution ? 'is-active' : ''
              }`}
              aria-pressed={config.projection.physicalFollowsResolution}
              title="Scale physical dimensions when resolution changes"
              onClick={() =>
                updateProjection({
                  physicalFollowsResolution: !config.projection.physicalFollowsResolution,
                })
              }
            >
              <span aria-hidden="true">🔗</span>
              {config.projection.physicalFollowsResolution
                ? 'Follows resolution'
                : 'Follow resolution'}
            </button>
          </div>
          <div className="field-row">
            <NumberField
              label={`Physical width (${config.displayUnit})`}
              value={config.projection.physical.widthMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateProjection({
                  physical: { ...config.projection.physical, widthMm: value * unitFactor },
                })
              }
            />
            <NumberField
              label={`Physical height (${config.displayUnit})`}
              value={config.projection.physical.heightMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateProjection({
                  physical: { ...config.projection.physical, heightMm: value * unitFactor },
                })
              }
            />
          </div>
          <div className="control-subheading control-subheading--spaced">Grid</div>
          <label className="field">
            <span>Define grid by</span>
            <select
              value={gridDefinitionMode}
              onChange={(event) => {
                const mode = event.target.value;
                if (mode === 'divisions') {
                  updateProjection({
                    gridPhysicalEnabled: false,
                    gridMode: 'fit',
                    gridLinkCounts: false,
                    gridColumns: effectiveGridColumns,
                    gridRows: effectiveGridRows,
                  });
                } else if (mode === 'pixels') {
                  updateProjection({ gridPhysicalEnabled: false, gridMode: 'square-from-x' });
                } else {
                  updateProjection({ gridPhysicalEnabled: true });
                }
              }}
            >
              <option value="divisions">Number of cells</option>
              <option value="pixels">Cell size in pixels</option>
              <option value="physical">Physical cell size</option>
            </select>
          </label>
          {gridDefinitionMode === 'divisions' && (
            <div className="field-row">
              <NumberField
                label="Columns"
                value={effectiveGridColumns}
                min={2}
                max={256}
                step={1}
                integer
                onChange={(gridColumns) => updateProjection({ gridColumns, gridLinkCounts: false })}
              />
              <NumberField
                label="Rows"
                value={effectiveGridRows}
                min={2}
                max={256}
                step={1}
                integer
                onChange={(gridRows) => updateProjection({ gridRows, gridLinkCounts: false })}
              />
            </div>
          )}
          {gridDefinitionMode === 'pixels' && (
            <>
              <Toggle
                label="Square cells"
                checked={pixelSquareCells}
                onChange={(checked) =>
                  updateProjection({
                    gridMode: checked ? 'square-from-x' : 'custom',
                    ...(checked ? { gridCellHeightPx: config.projection.gridCellWidthPx } : {}),
                  })
                }
              />
              {pixelSquareCells ? (
                <NumberField
                  label="Cell size"
                  value={
                    config.projection.gridMode === 'square-from-y'
                      ? config.projection.gridCellHeightPx
                      : config.projection.gridCellWidthPx
                  }
                  min={minimumSquareCellPx}
                  max={maximumSquareCellPx}
                  step={1}
                  suffix="px"
                  onChange={(cellSize) =>
                    updateProjection({
                      gridMode: 'square-from-x',
                      gridCellWidthPx: cellSize,
                      gridCellHeightPx: cellSize,
                    })
                  }
                />
              ) : (
                <div className="field-row">
                  <NumberField
                    label="Cell width"
                    value={config.projection.gridCellWidthPx}
                    min={minimumCellWidthPx}
                    max={Math.max(1, config.projection.resolution.width / 2)}
                    step={1}
                    suffix="px"
                    onChange={(gridCellWidthPx) => updateProjection({ gridCellWidthPx })}
                  />
                  <NumberField
                    label="Cell height"
                    value={config.projection.gridCellHeightPx}
                    min={minimumCellHeightPx}
                    max={Math.max(1, config.projection.resolution.height / 2)}
                    step={1}
                    suffix="px"
                    onChange={(gridCellHeightPx) => updateProjection({ gridCellHeightPx })}
                  />
                </div>
              )}
            </>
          )}
          {gridDefinitionMode === 'physical' && (
            <>
              <Toggle
                label="Square cells"
                checked={physicalSquareCells}
                onChange={(checked) => {
                  if (checked) {
                    const squareSize = Math.min(
                      maximumPhysicalSquareMm,
                      Math.max(minimumPhysicalSquareMm, config.projection.gridPhysicalWidthMm),
                    );
                    updateProjection({
                      gridPhysicalWidthMm: squareSize,
                      gridPhysicalHeightMm: squareSize,
                    });
                  } else {
                    updateProjection({
                      gridPhysicalHeightMm: Math.min(
                        config.projection.physical.heightMm / 2,
                        config.projection.gridPhysicalHeightMm * 0.8,
                      ),
                    });
                  }
                }}
              />
              {physicalSquareCells ? (
                <NumberField
                  label={`Cell size (${config.displayUnit})`}
                  value={config.projection.gridPhysicalWidthMm / unitFactor}
                  min={minimumPhysicalSquareMm / unitFactor}
                  max={maximumPhysicalSquareMm / unitFactor}
                  step={unitStep}
                  suffix={config.displayUnit}
                  onChange={(value) =>
                    updateProjection({
                      gridPhysicalWidthMm: value * unitFactor,
                      gridPhysicalHeightMm: value * unitFactor,
                    })
                  }
                />
              ) : (
                <div className="field-row">
                  <NumberField
                    label={`Cell width (${config.displayUnit})`}
                    value={config.projection.gridPhysicalWidthMm / unitFactor}
                    min={Math.max(unitStep, config.projection.physical.widthMm / 256 / unitFactor)}
                    max={Math.max(unitStep, config.projection.physical.widthMm / 2 / unitFactor)}
                    step={unitStep}
                    suffix={config.displayUnit}
                    onChange={(value) =>
                      updateProjection({ gridPhysicalWidthMm: value * unitFactor })
                    }
                  />
                  <NumberField
                    label={`Cell height (${config.displayUnit})`}
                    value={config.projection.gridPhysicalHeightMm / unitFactor}
                    min={Math.max(unitStep, config.projection.physical.heightMm / 256 / unitFactor)}
                    max={Math.max(unitStep, config.projection.physical.heightMm / 2 / unitFactor)}
                    step={unitStep}
                    suffix={config.displayUnit}
                    onChange={(value) =>
                      updateProjection({ gridPhysicalHeightMm: value * unitFactor })
                    }
                  />
                </div>
              )}
            </>
          )}
          <div className="grid-result" aria-live="polite">
            <span>Result</span>
            <strong>
              {projectionGridLayout.columns} × {projectionGridLayout.rows} cells
            </strong>
            <strong>
              {formatGridDimension(projectionGridLayout.stepX)} ×{' '}
              {formatGridDimension(projectionGridLayout.stepY)} px
            </strong>
            <small className="grid-result__physical">
              {formatGridDimension(physicalGridStepX)} × {formatGridDimension(physicalGridStepY)}{' '}
              {config.displayUnit}
            </small>
            {projectionGridLayout.limited && (
              <small>Requested size was limited to keep a visible 2–256 cell checker.</small>
            )}
          </div>
        </CollapsibleControlSection>
      ) : (
        <CollapsibleControlSection
          title="LED wall geometry"
          summary={`${config.led.columns} × ${config.led.rows} cabinets · ${config.led.cabinetResolution.width * config.led.columns} × ${config.led.cabinetResolution.height * config.led.rows} px`}
          badge="2 px borders"
          isOpen={openSection === 'geometry'}
          onToggle={() => toggleSection('geometry')}
        >
          <div className="field-row">
            <NumberField
              label="Cabinet width (px)"
              value={config.led.cabinetResolution.width}
              min={1}
              max={maximumCabinetWidth}
              step={1}
              integer
              onChange={(width) =>
                updateLed({ cabinetResolution: { ...config.led.cabinetResolution, width } })
              }
            />
            <NumberField
              label="Cabinet height (px)"
              value={config.led.cabinetResolution.height}
              min={1}
              max={maximumCabinetHeight}
              step={1}
              integer
              onChange={(height) =>
                updateLed({ cabinetResolution: { ...config.led.cabinetResolution, height } })
              }
            />
          </div>
          <div className="field-row">
            <NumberField
              label={`Cabinet width (${config.displayUnit})`}
              value={config.led.cabinetPhysical.widthMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateLed({
                  cabinetPhysical: { ...config.led.cabinetPhysical, widthMm: value * unitFactor },
                })
              }
            />
            <NumberField
              label={`Cabinet height (${config.displayUnit})`}
              value={config.led.cabinetPhysical.heightMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateLed({
                  cabinetPhysical: { ...config.led.cabinetPhysical, heightMm: value * unitFactor },
                })
              }
            />
          </div>
          <div className="field-row">
            <NumberField
              label="Columns"
              value={config.led.columns}
              min={1}
              max={maximumColumns}
              step={1}
              integer
              onChange={(columns) => updateLed({ columns })}
            />
            <NumberField
              label="Rows"
              value={config.led.rows}
              min={1}
              max={maximumRows}
              step={1}
              integer
              onChange={(rows) => updateLed({ rows })}
            />
          </div>
        </CollapsibleControlSection>
      )}

      <CollapsibleControlSection
        title="Pattern"
        summary={patternPresetLabel}
        isOpen={openSection === 'pattern'}
        onToggle={() => toggleSection('pattern')}
      >
        {config.mode === 'projection' ? (
          <>
            <label className="field">
              <span>Projection color preset</span>
              <select
                value={config.projection.colorPreset}
                onChange={(event) => {
                  const preset = PROJECTION_COLOR_PRESETS.find(
                    (item) => item.id === event.target.value,
                  );
                  if (!preset) return;
                  updateProjection({
                    colorPreset: preset.id,
                    ...(preset.id === 'custom'
                      ? {}
                      : {
                          backgroundColorA: preset.backgroundA,
                          backgroundColorB: preset.backgroundB,
                          gridColor: preset.grid,
                          overlayColor: preset.overlay,
                        }),
                  });
                }}
              >
                {PROJECTION_COLOR_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Background</span>
              <select
                value={config.projection.backgroundMode}
                onChange={(event) =>
                  updateProjection({
                    backgroundMode: event.target
                      .value as GeneratorConfig['projection']['backgroundMode'],
                  })
                }
              >
                <option value="solid">Solid</option>
                <option value="checkerboard">Checkerboard</option>
                <option value="transparent">Transparent</option>
              </select>
            </label>
            <ColorField
              label={
                config.projection.backgroundMode === 'checkerboard' ? 'Background A' : 'Background'
              }
              value={config.projection.backgroundColorA}
              onChange={(backgroundColorA) =>
                updateProjection({ backgroundColorA, colorPreset: 'custom' })
              }
            />
            {config.projection.backgroundMode === 'checkerboard' && (
              <ColorField
                label="Background B"
                value={config.projection.backgroundColorB}
                onChange={(backgroundColorB) =>
                  updateProjection({ backgroundColorB, colorPreset: 'custom' })
                }
              />
            )}
            <ColorField
              label="Grid color"
              value={config.projection.gridColor}
              onChange={(gridColor) => updateProjection({ gridColor, colorPreset: 'custom' })}
            />
            <NumberField
              label="Grid line width"
              value={config.projection.gridLineWidth}
              min={1}
              max={32}
              step={1}
              integer
              suffix="px"
              onChange={(gridLineWidth) => updateProjection({ gridLineWidth })}
            />
            <div className="toggle-grid">
              <Toggle
                label="Diagonals"
                checked={config.projection.showDiagonals}
                onChange={(showDiagonals) => updateProjection({ showDiagonals })}
              />
              <Toggle
                label="Center cross"
                checked={config.projection.showCenter}
                onChange={(showCenter) => updateProjection({ showCenter })}
              />
              <Toggle
                label="Circle"
                checked={config.projection.showCircle}
                onChange={(showCircle) => updateProjection({ showCircle })}
              />
            </div>
            <ColorField
              label="Overlay color"
              value={config.projection.overlayColor}
              onChange={(overlayColor) => updateProjection({ overlayColor, colorPreset: 'custom' })}
            />
          </>
        ) : (
          <>
            <label className="field">
              <span>LED color preset</span>
              <select
                value={config.led.colorPreset}
                onChange={(event) => {
                  const preset = LED_COLOR_PRESETS.find((item) => item.id === event.target.value);
                  if (!preset) return;
                  updateLed({
                    colorPreset: preset.id,
                    ...(preset.id === 'custom'
                      ? {}
                      : { backgroundColorA: preset.a, backgroundColorB: preset.b }),
                  });
                }}
              >
                {LED_COLOR_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            <ColorField
              label="Checker color A"
              value={config.led.backgroundColorA}
              onChange={(backgroundColorA) =>
                updateLed({ backgroundColorA, colorPreset: 'custom' })
              }
            />
            <ColorField
              label="Checker color B"
              value={config.led.backgroundColorB}
              onChange={(backgroundColorB) =>
                updateLed({ backgroundColorB, colorPreset: 'custom' })
              }
            />
            <ColorField
              label="Grid color"
              value={config.led.gridColor}
              onChange={(gridColor) => updateLed({ gridColor, colorPreset: 'custom' })}
            />
            <Toggle
              label="Cabinet coordinates (X,Y)"
              checked={config.led.showCoordinates}
              onChange={(showCoordinates) => updateLed({ showCoordinates })}
            />
            {config.led.showCoordinates && (
              <ColorField
                label="Coordinate color"
                value={config.led.coordinateColor}
                onChange={(coordinateColor) =>
                  updateLed({ coordinateColor, colorPreset: 'custom' })
                }
              />
            )}
          </>
        )}
      </CollapsibleControlSection>

      {config.mode === 'led' && allowPowerRoutes && (
        <CollapsibleControlSection
          title="Power cabling"
          summary={`${config.power.routes.length} saved route${config.power.routes.length === 1 ? '' : 's'}`}
          badge={config.power.routes.length}
          isOpen={openSection === 'power'}
          onToggle={() => toggleSection('power')}
          className="power-controls"
        >
          <p className="control-hint">
            Select <strong>+ Add cable</strong> above the canvas. Name and drawing controls will
            stay beside the preview.
          </p>
          <details className="advanced-grid power-defaults">
            <summary>Defaults for new cables</summary>
            <div className="advanced-grid__content">
              <ColorField
                label="Line color"
                value={config.power.lineColor}
                onChange={(lineColor) => updatePower({ lineColor })}
              />
              <NumberField
                label="Line width"
                value={config.power.lineWidth}
                min={2}
                max={80}
                step={1}
                integer
                suffix="px"
                onChange={(lineWidth) => updatePower({ lineWidth })}
              />
              <Toggle
                label="Incoming power cable"
                checked={config.power.incoming}
                onChange={(incoming) => updatePower({ incoming })}
              />
            </div>
          </details>
          {config.power.routes.length > 0 && (
            <div className="route-list" aria-label="Power routes">
              {config.power.routes.map((route, index) => (
                <div className="route-item" key={route.id}>
                  <span className="route-swatch" style={{ background: route.color }} />
                  <span>
                    {editingRouteId === route.id ? (
                      <input
                        className="route-name-input"
                        value={routeNameDraft}
                        maxLength={32}
                        aria-label={`Cable ${index + 1} name`}
                        autoFocus
                        onChange={(event) => setRouteNameDraft(event.target.value)}
                        onBlur={() => saveRouteName(route.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                          if (event.key === 'Escape') {
                            setEditingRouteId(null);
                            setRouteNameDraft('');
                          }
                        }}
                      />
                    ) : (
                      <strong>{route.cableLabel || `Cable ${index + 1}`}</strong>
                    )}
                    <small>
                      {route.points.length} cabinet{route.points.length === 1 ? '' : 's'}
                    </small>
                  </span>
                  <span className="route-actions">
                    <button
                      type="button"
                      aria-label={`Rename cable ${index + 1}`}
                      title="Rename cable"
                      onClick={() => {
                        setEditingRouteId(route.id);
                        setRouteNameDraft(route.cableLabel);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete route ${index + 1}`}
                      title="Delete route"
                      onClick={() =>
                        updatePower({
                          routes: config.power.routes.filter((item) => item.id !== route.id),
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                </div>
              ))}
              <button
                type="button"
                className="button button--ghost button--danger clear-routes"
                onClick={() => updatePower({ routes: [] })}
              >
                Clear all routes
              </button>
            </div>
          )}
        </CollapsibleControlSection>
      )}

      <CollapsibleControlSection
        title="Center label"
        summary={config.label.enabled ? 'Visible' : 'Hidden'}
        badge={config.label.enabled ? 'On' : undefined}
        isOpen={openSection === 'label'}
        onToggle={() => toggleSection('label')}
      >
        <Toggle
          label="Show label"
          checked={config.label.enabled}
          onChange={(enabled) => updateLabel({ enabled })}
        />
        {config.label.enabled && (
          <>
            <ColorField
              label="Text color"
              value={config.label.textColor}
              onChange={(textColor) => updateLabel({ textColor })}
            />
            <RangeField
              label="Font size"
              value={config.label.fontSize}
              min={10}
              max={500}
              step={1}
              formatValue={(value) => `${Math.round(value)} px`}
              onChange={(fontSize) => updateLabel({ fontSize })}
            />
            <ColorField
              label="Label background"
              value={config.label.backgroundColor}
              onChange={(backgroundColor) => updateLabel({ backgroundColor })}
            />
            <RangeField
              label="Background opacity"
              value={config.label.backgroundOpacity}
              onChange={(backgroundOpacity) => updateLabel({ backgroundOpacity })}
            />
          </>
        )}
      </CollapsibleControlSection>

      <CollapsibleControlSection
        title="Company logo"
        summary={config.logo.fileName || 'No logo selected'}
        badge="PNG"
        isOpen={openSection === 'logo'}
        onToggle={() => toggleSection('logo')}
      >
        <label className="file-picker">
          <input
            type="file"
            accept="image/png"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onLogoUpload(file);
              event.target.value = '';
            }}
          />
          <span>{config.logo.fileName || 'Choose a transparent PNG'}</span>
        </label>
        {config.logo.dataUrl && (
          <>
            <RangeField
              label="Logo size"
              value={config.logo.scale}
              min={0.02}
              max={0.3}
              step={0.01}
              onChange={(scale) => updateLogo({ scale })}
            />
            <RangeField
              label="Logo opacity"
              value={config.logo.opacity}
              onChange={(opacity) => updateLogo({ opacity })}
            />
            <button
              type="button"
              className="button button--ghost button--danger"
              onClick={() => updateLogo({ dataUrl: null, fileName: null })}
            >
              Remove logo
            </button>
          </>
        )}
      </CollapsibleControlSection>
    </div>
  );
}

function formatGridDimension(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
}

function formatPhysicalSize(
  widthMm: number,
  heightMm: number,
  unit: GeneratorConfig['displayUnit'],
): string {
  const factor = unit === 'm' ? 1000 : 1;
  return `${formatPhysicalDimension(widthMm / factor)} × ${formatPhysicalDimension(heightMm / factor)} ${unit}`;
}

function formatPhysicalDimension(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/\.?0+$/, '');
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  integer = false,
}: NumberFieldProps) {
  const [draft, setDraft] = useState(String(value));
  const errorId = useId();
  const error = validateNumberDraft(draft, { min, max, integer });

  useEffect(() => {
    const timer = window.setTimeout(() => setDraft(String(value)), 0);
    return () => window.clearTimeout(timer);
  }, [value]);

  return (
    <label className="field">
      <span>{label}</span>
      <div className="input-with-suffix">
        <input
          type="number"
          value={draft}
          min={min}
          max={max}
          step={step}
          inputMode="decimal"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const nextDraft = event.target.value;
            setDraft(nextDraft);
            const parsed = parseNumberDraft(nextDraft, { min, max, integer });
            if (parsed !== null) onChange(parsed);
          }}
          onBlur={() => {
            if (error) setDraft(String(value));
          }}
        />
        {suffix && <span>{suffix}</span>}
      </div>
      {error && (
        <small className="field-error" id={errorId}>
          {error}
        </small>
      )}
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const normalizedValue = value.toUpperCase();
  const [draft, setDraft] = useState(normalizedValue);
  const errorId = useId();
  const error = validateHexColor(draft);

  useEffect(() => {
    const timer = window.setTimeout(() => setDraft(normalizedValue), 0);
    return () => window.clearTimeout(timer);
  }, [normalizedValue]);

  return (
    <label className="field color-field">
      <span>{label}</span>
      <span className="color-input">
        <input
          type="color"
          value={value}
          onChange={(event) => {
            const nextValue = event.target.value.toUpperCase();
            setDraft(nextValue);
            onChange(nextValue);
          }}
        />
        <input
          className="color-text"
          value={draft}
          maxLength={7}
          spellCheck={false}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const nextDraft = event.target.value.toUpperCase();
            setDraft(nextDraft);
            if (!validateHexColor(nextDraft)) onChange(nextDraft);
          }}
          onBlur={() => {
            if (error) setDraft(normalizedValue);
          }}
          aria-label={`${label} hex value`}
        />
      </span>
      {error && (
        <small className="field-error color-field-error" id={errorId}>
          {error}
        </small>
      )}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

function RangeField({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  formatValue = (currentValue) => `${Math.round(currentValue * 100)}%`,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
}) {
  return (
    <label className="field range-field">
      <span>
        {label} <output>{formatValue(value)}</output>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          const nextValue = Number(event.target.value);
          if (Number.isFinite(nextValue)) onChange(Math.min(max, Math.max(min, nextValue)));
        }}
      />
    </label>
  );
}

function presetValue(width: number, height: number): string {
  const value = `${width}x${height}`;
  return RESOLUTION_PRESETS.some((preset) => `${preset.width}x${preset.height}` === value)
    ? value
    : '0x0';
}
